import { useRef, useState, type TouchEvent } from "react";

// Swipe a message row on a touch screen: right marks it read or unread, left archives it, as in Outlook's phone app.
// Touch events only, so a mouse never triggers it; the row is `touch-action: pan-y`, so up-and-down still scrolls the list.

/** How far a swipe must travel (px) to count. */
export const SWIPE_COMMIT = 90;
const MAX = 160;
/** Movement before we decide whether this is a swipe or a scroll. */
const LOCK_AT = 10;

interface Handlers {
    /** Finger moved right. */
    right?: () => void;
    /** Finger moved left. */
    left?: () => void;
}

export function useRowSwipe(handlers: Handlers | null) {
    const [dx, setDx] = useState(0);
    const [dragging, setDragging] = useState(false);
    // The distance as of the last move, read on release: a state updater must stay pure, and this runs an action.
    const dxRef = useRef(0);
    const start = useRef<{ x: number; y: number; horizontal: boolean | null } | null>(null);
    const latest = useRef(handlers);
    latest.current = handlers;

    const onTouchStart = (e: TouchEvent) => {
        if (!latest.current || e.touches.length !== 1) return;
        const t = e.touches[0]!;
        start.current = { x: t.clientX, y: t.clientY, horizontal: null };
    };

    const onTouchMove = (e: TouchEvent) => {
        const s = start.current;
        if (!s) return;
        const t = e.touches[0]!;
        const mx = t.clientX - s.x;
        const my = t.clientY - s.y;
        if (s.horizontal === null) {
            if (Math.abs(mx) < LOCK_AT && Math.abs(my) < LOCK_AT) return;
            s.horizontal = Math.abs(mx) > Math.abs(my);
            if (s.horizontal) setDragging(true);
        }
        if (!s.horizontal) return;
        const allowed = (mx > 0 && latest.current?.right) || (mx < 0 && latest.current?.left);
        dxRef.current = allowed ? Math.max(-MAX, Math.min(MAX, mx)) : 0;
        setDx(dxRef.current);
    };

    const finish = (commit: boolean) => {
        const s = start.current;
        start.current = null;
        setDragging(false);
        const moved = dxRef.current;
        dxRef.current = 0;
        setDx(0);
        if (commit && s?.horizontal) {
            if (moved >= SWIPE_COMMIT) latest.current?.right?.();
            else if (moved <= -SWIPE_COMMIT) latest.current?.left?.();
        }
    };

    return {
        dx,
        dragging,
        bind: handlers
            ? { onTouchStart, onTouchMove, onTouchEnd: () => finish(true), onTouchCancel: () => finish(false) }
            : {}
    };
}
